/*
 * Elexa header behaviour (sections/header.liquid).
 * - From 990 px, with a mouse, dropdown and mega panels open on hover and close 150 ms after the
 *   pointer leaves the item and its panel (a CSS bridge covers the 5 px gap). Touch: tap toggles.
 *   Click and keyboard keep Dawn's details/summary behaviour (Enter/Space toggle, Escape closes).
 * - Floating panels are centred under their item and kept 15 px inside the viewport.
 * Delegated listeners, so the header keeps working after a theme-editor re-render.
 */
(() => {
  if (window.Elexa && window.Elexa.headerLoaded) return;
  window.Elexa = window.Elexa || {};
  window.Elexa.headerLoaded = true;

  const desktop = window.matchMedia('(min-width: 990px)');
  const CLOSE_DELAY = 150;
  const EDGE = 15;
  const timers = new WeakMap();

  const detailsOf = (menu) => menu && menu.querySelector(':scope > details');
  const summaryOf = (details) => details && details.querySelector(':scope > summary');

  function clamp(details) {
    const panel = details && details.querySelector(':scope > .ex-menu-panel');
    if (!panel || !desktop.matches) return;
    panel.style.setProperty('--ex-panel-shift', '0px');
    const rect = panel.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    let shift = 0;
    if (rect.width + EDGE * 2 >= vw || rect.left < EDGE) {
      shift = EDGE - rect.left;
    } else if (rect.right > vw - EDGE) {
      shift = vw - EDGE - rect.right;
    }
    if (shift) panel.style.setProperty('--ex-panel-shift', `${Math.round(shift)}px`);
  }

  function setOpen(details, open, viaHover = false) {
    if (!details) return;
    const summary = summaryOf(details);
    if (open) {
      document.querySelectorAll('.ex-hover-menu > details[open]').forEach((other) => {
        if (other !== details) setOpen(other, false);
      });
      if (!details.open) details.open = true;
      if (viaHover) details.dataset.exHover = 'true';
      if (summary) summary.setAttribute('aria-expanded', 'true');
      clamp(details);
    } else {
      delete details.dataset.exHover;
      if (details.open) details.open = false;
      if (summary) summary.setAttribute('aria-expanded', 'false');
    }
  }

  function cancelClose(menu) {
    clearTimeout(timers.get(menu));
    timers.delete(menu);
  }

  function scheduleClose(menu) {
    cancelClose(menu);
    timers.set(
      menu,
      setTimeout(() => {
        timers.delete(menu);
        setOpen(detailsOf(menu), false);
      }, CLOSE_DELAY)
    );
  }

  const isMouse = (event) => event.pointerType === 'mouse' && desktop.matches;

  document.addEventListener('pointerover', (event) => {
    if (!isMouse(event) || !event.target.closest) return;
    const menu = event.target.closest('.ex-header .ex-hover-menu');
    if (!menu || menu.contains(event.relatedTarget)) return;
    cancelClose(menu);
    const details = detailsOf(menu);
    if (details && !details.open) setOpen(details, true, true);
  });

  document.addEventListener('pointerout', (event) => {
    if (!isMouse(event) || !event.target.closest) return;
    const menu = event.target.closest('.ex-header .ex-hover-menu');
    if (!menu || menu.contains(event.relatedTarget)) return;
    scheduleClose(menu);
  });

  // A click on an item that the pointer just opened keeps it open (instead of toggling it shut);
  // the next click toggles as usual.
  document.addEventListener(
    'click',
    (event) => {
      if (!event.target.closest) return;
      const summary = event.target.closest('.ex-header .ex-hover-menu > details > summary');
      if (!summary) return;
      const details = summary.parentElement;
      if (details.open && details.dataset.exHover === 'true') {
        event.preventDefault();
        event.stopPropagation();
        delete details.dataset.exHover;
        summary.setAttribute('aria-expanded', 'true');
      }
    },
    true
  );

  // Clamp panels however they were opened (click, keyboard, hover). When a panel closes, its nested
  // (third-level) lists close too, so the panel opens clean next time.
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      if (!details.parentElement || !details.parentElement.classList.contains('ex-hover-menu')) return;
      if (details.open) {
        clamp(details);
        return;
      }
      details.querySelectorAll('details[open]').forEach((nested) => {
        nested.open = false;
        const nestedSummary = summaryOf(nested);
        if (nestedSummary) nestedSummary.setAttribute('aria-expanded', 'false');
      });
    },
    true
  );

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      document.querySelectorAll('.ex-hover-menu > details[open]').forEach((details) => {
        if (desktop.matches) clamp(details);
        else setOpen(details, false);
      });
    }, 250);
  });
})();
